import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { BookUser, MapPin, BookOpen, Volume2, Loader2, Square, Star, Sparkles, Gamepad2 } from "lucide-react";
import { api, money, USER_ID, mkAudio, track } from "../api";
import { useSeo } from "../seo";
import { DishCard, SectionTitle } from "../components";
import { newGuest } from "../api";
import { mascotHidden, setMascotHidden } from "../Mascot";
import { CATEGORIES } from "./MenuPage";
import { useFlags, flagOn } from "../flags";
import { useT, useLang, tCat } from "../i18n";
import { ReviewThanks } from "../ReviewThanks";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ExperienceHome() {
  const t = useT();
  useSeo({
    description: "Urban by VeerGi — Punjabi restaurant in Lincoln Park, Chicago (Hoffman Estates coming soon). Explore the menu, hear dish stories, earn Urban Points.",
    path: "/experience",
  });
  const { lang } = useLang();
  const flags = useFlags();
  const [featured, setFeatured] = useState([]);
  const [storyDish, setStoryDish] = useState(null);
  const [specials, setSpecials] = useState([]);
  const [tick, setTick] = useState(0);
  const [heroIdx, setHeroIdx] = useState(0);
  const [cine, setCine] = useState([{ slug: "butter-chicken", headline: "Butter Chicken", assets: { scene: "/cinematic/scene.webp", hero: "/cinematic/hero.webp" } }]);
  const [cineIdx, setCineIdx] = useState(0);
  useEffect(() => {
    api.get("/cinematic").then((r) => { if (r.data.experiences?.length) setCine(r.data.experiences); }).catch(() => {});
  }, []);
  useEffect(() => {
    if (cine.length < 2) return;
    const id = setInterval(() => setCineIdx((i) => i + 1), 6000);
    return () => clearInterval(id);
  }, [cine.length]);
  useEffect(() => {
    if (featured.length < 2) return;
    const id = setInterval(() => setHeroIdx((i) => i + 1), 5000);
    return () => clearInterval(id);
  }, [featured.length]);
  useEffect(() => {
    if (specials.length < 2) return;
    const id = setInterval(() => setTick((x) => x + 1), 4000);
    return () => clearInterval(id);
  }, [specials.length]);
  const [speaking, setSpeaking] = useState(null);
  const audioRef = useRef(null);
  useEffect(() => () => audioRef.current?.pause(), []);

  const speakSpecial = async (e, brand) => {
    e.preventDefault();
    e.stopPropagation();
    if (speaking === brand) {
      audioRef.current?.pause();
      setSpeaking(null);
      return;
    }
    try {
      setSpeaking(`loading-${brand}`);
      const r = await api.post("/specials/speak", { brand, user_id: USER_ID });
      if (!r.data.audio_url) return setSpeaking(null);
      audioRef.current?.pause();
      const a = mkAudio(`${BACKEND_URL}${r.data.audio_url}`);
      audioRef.current = a;
      a.onended = () => setSpeaking(null);
      a.play().catch(() => setSpeaking(null));
      setSpeaking(brand);
    } catch {
      setSpeaking(null);
    }
  };
  const [guestConfirm, setGuestConfirm] = useState(false);
  const [mascotOff, setMascotOff] = useState(mascotHidden);

  useEffect(() => {
    const sync = () => setMascotOff(mascotHidden());
    window.addEventListener("xp-mascot-pref", sync);
    return () => window.removeEventListener("xp-mascot-pref", sync);
  }, []);

  const toggleMascot = () => {
    setMascotHidden(!mascotOff);
    setMascotOff(!mascotOff);
  };

  const [reviewUrl, setReviewUrl] = useState("");
  const [extDishes, setExtDishes] = useState([]);
  const [thanksOpen, setThanksOpen] = useState(false);
  useEffect(() => {
    api.get("/dishes", { params: { featured: true } }).then((r) => setFeatured(r.data.slice(0, 8)));
    api.get("/dishes/butter-chicken").then((r) => r.data.story && setStoryDish(r.data)).catch(() => {});
    api.get("/brand-specials").then((r) => setSpecials(r.data.specials || [])).catch(() => {});
    api.get("/settings").then((r) => setReviewUrl(r.data.review_url || "")).catch(() => {});
    api.get("/ext-menu").then((r) => setExtDishes(r.data || [])).catch(() => {});
  }, []);

  const handleNewGuest = () => {
    if (!guestConfirm) {
      setGuestConfirm(true);
      setTimeout(() => setGuestConfirm(false), 3000);
      return;
    }
    newGuest();
  };

  return (
    <div data-testid="experience-home">
      {/* HERO — grand entrance with rotating featured dishes */}
      <div className="relative overflow-hidden onphoto" data-testid="home-hero">
        <div className="absolute inset-0 bg-[#0B0B0B]" />
        {featured.length > 0 && (
          <div className="absolute inset-0" data-testid="hero-rotator" aria-hidden="true">
            {featured.map((f, i) => (
              <img key={f.slug} src={f.image} alt="" data-testid={`hero-rotator-img-${i}`}
                className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${i === heroIdx % featured.length ? "opacity-100" : "opacity-0"}`} />
            ))}
          </div>
        )}
        <div className="absolute inset-0" style={{ background: "radial-gradient(110% 85% at 88% 100%, rgba(212,175,55,0.15), transparent 55%), linear-gradient(100deg, rgba(8,8,8,0.96) 0%, rgba(8,8,8,0.85) 45%, rgba(8,8,8,0.5) 100%)" }} />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" />
        <div className="relative max-w-6xl mx-auto">
          <div className="relative z-10 px-5 pt-8 pb-7 pr-28 sm:pr-40 lg:pr-64 lg:px-10 lg:pt-12 lg:pb-10">
            <img src="/urban-logo-lg.png" alt="Urban by VeerGi" data-testid="hero-logo"
              className="h-24 sm:h-28 lg:h-32 w-auto mb-4 hero-fade-in" style={{ filter: "drop-shadow(0 0 26px rgba(212,175,55,0.28))" }} />
            <p className="xp-eyebrow text-xs tracking-[0.35em] font-bold mb-2 hero-fade-in" style={{ animationDelay: "120ms" }}>THE WORLD OF PUNJABI FOOD</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-tight hero-fade-in" style={{ animationDelay: "200ms" }}>{t("home.h1")}</h1>
            <p className="text-base leading-relaxed mt-3 max-w-md hero-fade-in" style={{ animationDelay: "300ms" }}>{t("home.sub")}</p>
            <div className="relative z-20 flex flex-wrap gap-3 mt-6 hero-fade-in" style={{ animationDelay: "400ms" }}>
              <Link data-testid="hero-order-table" to="/t" className="h-12 px-7 rounded-full bg-[#E8743B] text-white font-bold text-sm tracking-widest flex items-center hover:opacity-90 transition-opacity">ORDER AT MY TABLE</Link>
              <Link data-testid="hero-browse-menu" to="/menu" className="h-12 px-7 rounded-full bg-[#D4AF37] text-black font-bold text-sm tracking-widest flex items-center hover:bg-[#F0C742] transition-colors">{t("home.browse")}</Link>
              <Link data-testid="hero-urban-arcade" to="/experience/arcade" className="h-12 px-7 rounded-full border-2 border-[#D4AF37]/70 font-bold text-sm tracking-widest flex items-center">{t("home.arcadeTab")}</Link>
              <Link data-testid="hero-play-earn" to="/experience/passport" className="h-12 px-7 rounded-full border-2 border-[#D4AF37]/70 font-bold text-sm tracking-widest flex items-center">{t("home.play")}</Link>
            </div>
          </div>
          <img src="/mascot/wave.png" alt="VeerGi welcomes you" data-testid="hero-mascot"
            className="hero-mascot absolute bottom-0 right-1 sm:right-4 lg:right-10 w-28 sm:w-36 lg:w-48 h-auto z-10" />
        </div>
      </div>

      {/* TODAY'S PICK TICKER */}
      {specials.length > 0 && (() => {
        const s = specials[tick % specials.length];
        return (
          <Link to={`/experience/brands/${s.brand}`} data-testid="hero-specials-ticker"
            className="block bg-[#14100A] border-b border-[#D4AF37]/20 overflow-hidden">
            <div className="max-w-6xl mx-auto w-full flex items-center gap-2 px-5 lg:px-10 h-9">
              <span className="w-1.5 h-1.5 rounded-full bg-[#D4AF37] animate-pulse shrink-0" />
              <p key={tick} data-testid="hero-ticker-line" className="ticker-line text-[11px] tracking-wide text-white/80 truncate min-w-0">
                <span className="text-[#D4AF37] font-black">TODAY:</span> {s.item.name} at {s.brand_name} · <span className="text-[#D4AF37] font-bold">{money(s.item.price)}</span>
              </p>
            </div>
          </Link>
        );
      })()}

      <div className="mx-auto w-full max-w-6xl">

      {/* TWO CHOICES */}
      <section className="px-4 pt-6 grid grid-cols-2 gap-3" data-testid="section-two-choices">
        <Link to="/menu" data-testid="choice-menu" className="rounded-2xl bg-[#121212] border border-white/10 p-4 xp-card-shadow">
          <img src="/urban-emblem.png" alt="Urban emblem" data-testid="choice-menu-logo" className="h-10 w-auto" />
          <p className="font-display text-xl font-bold mt-2">{t("home.choiceMenuTitle")}</p>
          <p className="text-sm text-white/60 mt-1">{t("home.choiceMenuDesc")}</p>
        </Link>
        <Link to="/experience/passport" data-testid="choice-passport" className="rounded-2xl p-4 border border-[#D4AF37]/40 bg-gradient-to-br from-[#1F1F1F] to-[#141414] xp-card-shadow-lg">
          <img src="/passport-seal.png" alt="Urban Passport seal" data-testid="choice-passport-seal" className="h-12 w-12" style={{ filter: "drop-shadow(0 2px 8px rgba(212,175,55,0.35))" }} />
          <p className="font-display text-xl font-bold mt-2">Urban Passport</p>
          <p className="text-sm text-white/60 mt-1">{t("home.choicePassportDesc")}</p>
        </Link>
      </section>

      <div className="xp-divider" />

      {/* THE URBAN ARCADE */}
      {flagOn(flags, "arcade") && (
      <section className="px-4 pt-4" data-testid="section-arcade">
        <Link to="/experience/arcade" data-testid="home-arcade-card"
          className="relative block overflow-hidden rounded-2xl border border-[#E25822]/40 bg-gradient-to-r from-[#1C0F08] via-[#121212] to-[#1A1408] p-5 xp-card-shadow-lg">
          <div className="absolute -top-10 right-8 w-40 h-24 rounded-full bg-[#E25822]/15 blur-3xl pointer-events-none" />
          <div className="flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.3em] text-[#E25822] font-bold">NEW · ਪੁਰਾਣਾ ਪੰਜਾਬ ਮੇਲਾ</p>
              <p className="font-display text-2xl font-bold mt-1">Urban Arcade — Purana Punjab Mela</p>
              <p className="text-sm text-white/60 mt-1">Step back into the Punjab we remember — spin, scratch, match masalas & win Regal Coins.</p>
            </div>
            <Gamepad2 size={40} className="text-[#D4AF37] shrink-0" strokeWidth={1.5} />
          </div>
          <span className="inline-flex items-center h-10 px-6 mt-3 rounded-full bg-[#E25822] text-white font-black text-xs tracking-widest">PLAY NOW</span>
        </Link>
      </section>
      )}

      {/* CINEMATIC DISH EXPERIENCE */}
      {(() => { const ce = cine[cineIdx % cine.length]; return (
      <section className="px-4 pt-4" data-testid="section-cinematic">
        <Link to={`/experience/cinematic/${ce.slug}`} data-testid="home-cinematic-card"
          className="relative block overflow-hidden rounded-2xl border border-[#D4AF37]/40 xp-card-shadow-lg">
          <img src={ce.assets.scene} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-70" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/55 to-transparent" />
          <div key={ce.slug} className="relative p-5 flex items-center justify-between gap-4 attract-fade">
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold">NEW · CINEMATIC EXPERIENCE</p>
              <p className="font-display text-2xl font-bold mt-1">{ce.headline}, Like Never Before</p>
              <p className="text-sm text-white/65 mt-1">Watch the dish land, the steam rise — then hear its story.</p>
              <span className="inline-flex items-center h-10 px-6 mt-3 rounded-full bg-[#D4AF37] text-black font-black text-xs tracking-widest">STEP INSIDE</span>
              {cine.length > 1 && (
                <span className="flex gap-1.5 mt-3">
                  {cine.map((c, i) => (
                    <span key={c.slug} data-testid={`home-cinematic-dot-${c.slug}`} className={`w-1.5 h-1.5 rounded-full ${i === cineIdx % cine.length ? "bg-[#D4AF37]" : "bg-white/25"}`} />
                  ))}
                </span>
              )}
            </div>
            <img src={ce.assets.hero} alt={ce.headline} className="w-24 h-24 sm:w-28 sm:h-28 object-contain shrink-0 drop-shadow-2xl" loading="lazy" />
          </div>
        </Link>
      </section>
      ); })()}

      {/* MENU CATEGORIES */}
      <section className="px-4" data-testid="section-categories">
        <SectionTitle editKey="home.categories" kicker={t("home.menuKicker")} title={t("home.menuTitle")} testId="section-categories-title" />
        <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2">
          {CATEGORIES.map((c) => (
            <Link key={c} data-testid={`home-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} to={c === "Popular" ? "/menu" : `/menu?cat=${encodeURIComponent(c)}`}
              className="h-14 rounded-xl bg-[#121212] border border-white/10 flex items-center justify-center text-center text-sm font-bold px-1 leading-tight">
              {tCat(lang, c)}
            </Link>
          ))}
        </div>
      </section>

      {/* TODAY AT THE URBAN FAMILY */}
      {specials.length > 0 && (
        <section className="pt-8" data-testid="section-brand-specials">
          <div className="px-4">
            <SectionTitle editKey="home.brandSpecials" kicker="One passport · every Urban kitchen" title="Today at the Urban family" testId="section-brand-specials-title" />
          </div>
          <div className="flex gap-3 overflow-x-auto snap-x hide-scrollbar px-4 md:grid md:grid-cols-3 md:overflow-visible">
            {specials.map((s) => (
              <Link key={s.brand} to={`/experience/brands/${s.brand}`} data-testid={`brand-special-${s.brand}`}
                className="relative snap-start shrink-0 w-44 md:w-auto rounded-2xl bg-[#121212] border overflow-hidden xp-card-shadow"
                style={{ borderColor: `${s.accent}44` }}>
                <button data-testid={`brand-special-speak-${s.brand}`} onClick={(e) => speakSpecial(e, s.brand)}
                  aria-label={`Hear today's pick at ${s.brand_name}`}
                  className="absolute top-2 right-2 z-10 w-8 h-8 rounded-full bg-black/60 backdrop-blur flex items-center justify-center text-[#D4AF37] border border-[#D4AF37]/40">
                  {speaking === `loading-${s.brand}`
                    ? <Loader2 size={14} className="animate-spin" />
                    : speaking === s.brand
                      ? <Square size={12} fill="currentColor" />
                      : <Volume2 size={14} />}
                </button>
                {s.item.image && (
                  <img src={s.item.image} alt={s.item.name} loading="lazy" className="w-full h-28 object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                )}
                <div className="p-3">
                  <span className="text-[9px] font-black tracking-widest rounded-full px-2 py-0.5"
                    style={{ background: `${s.accent}22`, color: s.accent }}>
                    {s.brand_name.toUpperCase()}
                  </span>
                  <p className="text-sm font-bold leading-snug mt-2 line-clamp-2">{s.item.name}</p>
                  <p className="text-sm font-black text-[#D4AF37] mt-1">{money(s.item.price)}</p>
                </div>
              </Link>
            ))}
          </div>
          <p className="px-4 text-[10px] tracking-[0.25em] text-white/40 mt-2 font-bold">TODAY'S PICKS — FRESH ONES TOMORROW</p>
        </section>
      )}

      {/* FEATURED */}
      <section className="py-8" data-testid="section-featured">
        <div className="px-4"><SectionTitle editKey="home.featured" kicker={t("home.lovedKicker")} title={t("home.lovedTitle")} testId="section-featured-title" /></div>
        <div className="flex gap-3 overflow-x-auto snap-x hide-scrollbar px-4">
          {featured.map((dish) => <DishCard key={dish.slug} dish={dish} compact />)}
        </div>
      </section>

      {/* EXTENDED MENU (only when the owner toggled dishes on) */}
      {extDishes.length > 0 && (
        <section className="pb-8" data-testid="section-ext-menu">
          <div className="px-4 flex items-end justify-between gap-3">
            <SectionTitle editKey="home.extmenu" kicker="Beyond the regular menu" title="Extended Menu" testId="section-ext-menu-title" />
            <Link to="/menu?cat=Extended%20Menu" data-testid="home-ext-see-all"
              className="shrink-0 mb-4 text-sm font-bold text-[#D4AF37] underline underline-offset-4">SEE ALL →</Link>
          </div>
          <div className="flex gap-3 overflow-x-auto snap-x hide-scrollbar px-4">
            {extDishes.slice(0, 10).map((d) => (
              <Link key={d.pos_id} to="/menu?cat=Extended%20Menu" data-testid={`home-ext-${d.pos_id}`}
                className="snap-start shrink-0 w-40 rounded-2xl bg-[#121212] border border-[#D4AF37]/25 overflow-hidden xp-card-shadow">
                {d.image ? (
                  <img src={d.image} alt={d.name} loading="lazy" className="w-full h-24 object-cover"
                    onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <div className="w-full h-24 bg-[#1A1A1A] flex items-center justify-center text-[#D4AF37]/40"><Sparkles size={20} /></div>
                )}
                <div className="p-3">
                  <p className="text-sm font-bold leading-snug line-clamp-2">{d.name}</p>
                  <p className="text-sm font-black text-[#D4AF37] mt-1">{money(d.price)}</p>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* STORY PREVIEW */}
      {storyDish && (
        <section className="px-4 pb-8" data-testid="section-story-preview">
          <SectionTitle editKey="home.story" kicker={t("home.storyKicker")} title={t("home.storyTitle")} testId="section-story-title" />
          <Link to={`/experience/dish/${storyDish.slug}#story`} data-testid="home-story-card" className="block rounded-2xl bg-[#121212] border border-white/10 overflow-hidden xp-card-shadow-lg">
            <img src={storyDish.image} alt={storyDish.name} className="w-full h-40 object-cover" />
            <div className="p-5">
              <div className="flex items-center gap-2 text-[#D4AF37] mb-1"><BookOpen size={14} /><span className="text-xs tracking-[0.3em] font-bold">{storyDish.story.title.toUpperCase()}</span></div>
              <p className="text-base leading-relaxed text-white/75 line-clamp-3">{storyDish.story.cards[0].text}</p>
              <span className="inline-block mt-3 text-sm font-bold text-[#D4AF37] underline underline-offset-4">{t("home.readStory")}</span>
            </div>
          </Link>
        </section>
      )}

      {/* PASSPORT INVITATION */}
      <section className="px-4 pb-8" data-testid="section-passport-invite">
        <div className="relative overflow-hidden rounded-2xl p-6 text-center border border-[#D4AF37]/40 bg-gradient-to-br from-[#1F1F1F] to-[#141414] xp-card-shadow-lg">
          <div className="absolute -top-16 left-1/2 -translate-x-1/2 w-64 h-32 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
          <BookUser size={24} className="mx-auto text-[#D4AF37]" />
          <p className="font-display text-2xl font-bold mt-2">{t("home.inviteTitle")}</p>
          <p className="text-base text-white/60 mt-2">{t("home.inviteDesc")}</p>
          <Link data-testid="start-passport-adventure" to="/experience/passport"
            className="inline-flex items-center h-12 px-7 mt-4 rounded-full bg-[#D4AF37] text-black font-bold text-sm tracking-widest hover:bg-[#F0C742] transition-colors">
            {t("home.startAdventure")}
          </Link>
        </div>
      </section>

      {/* GOOGLE REVIEW ASK */}
      {reviewUrl && (
        <section className="px-4 pb-8" data-testid="section-google-review">
          <div data-testid="home-review-card" className="relative overflow-hidden rounded-2xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#1C160A] to-[#121212] p-6 xp-card-shadow-lg">
            <div className="absolute -top-14 right-6 w-48 h-28 rounded-full bg-[#D4AF37]/10 blur-3xl pointer-events-none" />
            <div className="flex items-center gap-1 text-[#D4AF37]" aria-hidden="true">
              {[0, 1, 2, 3, 4].map((i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-bold mt-3">FROM OUR FAMILY TO YOURS</p>
            <p className="font-display text-2xl font-bold mt-1">Loved your visit? Tell Google.</p>
            <p className="text-base text-white/70 leading-relaxed mt-2 max-w-xl">
              Your Google review reshapes our future in this community — it helps neighbours find us, keeps our kitchens
              cooking, and lets a small family business keep sharing the food of Punjab. Thirty seconds from you means
              the world to us.
            </p>
            <a
              data-testid="home-review-btn"
              href={reviewUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => { track("google_review_tapped", { source: "home" }); setThanksOpen(true); }}
              className="inline-flex items-center gap-2 h-12 px-7 mt-4 rounded-full bg-[#D4AF37] text-black font-bold text-sm tracking-widest hover:bg-[#F0C742] transition-colors"
            >
              <Star size={15} fill="currentColor" /> LEAVE A GOOGLE REVIEW
            </a>
          </div>
          <ReviewThanks open={thanksOpen} onClose={() => setThanksOpen(false)} />
        </section>
      )}

      {/* LOCATIONS + ORDERING */}
      <section className="px-4 pb-8" data-testid="section-locations-info">
        <Link to="/experience/locations" data-testid="home-locations-card" className="block rounded-2xl bg-[#121212] border border-white/10 p-5 xp-card-shadow">
          <div className="flex items-center gap-2 text-[#D4AF37] mb-2"><MapPin size={15} /><span className="text-xs tracking-[0.3em] font-bold">{t("home.visitUs")}</span></div>
          <p className="text-base">Lincoln Park — 2660 N. Halsted St, Chicago · 2–11 PM</p>
          <p className="text-base mt-1">Hoffman Estates — Opening October 2026</p>
          <span className="inline-block mt-3 text-sm font-bold text-[#D4AF37] underline underline-offset-4">{t("home.mapsLine")}</span>
        </Link>
        <p className="text-sm text-white/60 mt-3 text-center">{t("home.orderNote")}</p>
      </section>

      {/* FOOTER */}
      <footer className="px-4 pb-10 pt-4 border-t border-white/10 text-center" data-testid="home-footer">
        <p className="font-display text-lg font-bold">URBAN BY VEERGI</p>
        <p className="text-sm text-white/60 mt-1">
          The World of Punjabi Food
          {" · "}<a data-testid="footer-call-restaurant" href="tel:+13129550331" className="text-[#D4AF37] font-bold whitespace-nowrap">(312) 955-0331</a> restaurant
          {" · "}<a data-testid="footer-call-catering" href="tel:+13129550463" className="text-[#D4AF37] font-bold whitespace-nowrap">312-955-0463</a> catering
          {" · "}TryUrbanTurban.com
        </p>
        <div className="mt-4 flex items-center justify-center gap-6">
          {sessionStorage.getItem("xp_admin_ok") === "1" && (
            <Link to="/experience/admin" data-testid="admin-link" className="text-xs tracking-[0.25em] text-white/40">EXPERIENCE ADMIN</Link>
          )}
          <button data-testid="new-guest-btn" onClick={handleNewGuest}
            className={`text-xs tracking-[0.25em] ${guestConfirm ? "text-[#E25822] font-bold" : "text-white/40"}`}>
            {guestConfirm ? "TAP AGAIN TO CONFIRM" : "NEW GUEST"}
          </button>
          <button data-testid="mascot-toggle" onClick={toggleMascot} className="text-xs tracking-[0.25em] text-white/40">
            {mascotOff ? "SHOW VEERGI" : "HIDE VEERGI"}
          </button>
        </div>
      </footer>
      </div>
    </div>
  );
}
